import { __extends, __read, __spreadArray } from "tslib";
import Raw from './raw';
import util from 'util';
import dayjs from 'dayjs';
var invokableFunc = require('./utils/invokable');
export var LENGTH_VARIANTS;
(function (LENGTH_VARIANTS) {
    LENGTH_VARIANTS["tiny"] = "tiny";
    LENGTH_VARIANTS["empty"] = "";
    LENGTH_VARIANTS["medium"] = "medium";
    LENGTH_VARIANTS["long"] = "long";
})(LENGTH_VARIANTS || (LENGTH_VARIANTS = {}));
;
/**
 * @example
 * const { STRING, INTEGER, BIGINT, DATE, BOOLEAN } = app.model;
 * class User = app.model.define('User', {
 *   login: STRING,
 * });
 */
var DataType = /** @class */ (function () {
    function DataType() {
        this.dataType = '';
    }
    /**
     * cast raw data returned from data packet into js type
     */
    DataType.prototype.cast = function (value) {
        return value;
    };
    /**
     * uncast js value into database type with precision
     */
    DataType.prototype.uncast = function (value) {
        return value;
    };
    return DataType;
}());
export { DataType };
function hasDataLength(dataLength) {
    if (typeof dataLength === 'number') {
        return dataLength > 0;
    }
    return !!dataLength;
}
/**
 * @example
 * STRING
 * STRING(127)
 * STRING.BINARY
 * @param {number} dataLength
 */
var STRING = /** @class */ (function (_super) {
    __extends(STRING, _super);
    function STRING(dataLength) {
        if (dataLength === void 0) { dataLength = 255; }
        var _this = _super.call(this) || this;
        _this.dataType = 'varchar';
        _this.dataLength = dataLength;
        return _this;
    }
    STRING.prototype.toSqlString = function () {
        var dataLength = this.dataLength;
        var dataType = this.dataType.toUpperCase();
        var chunks = [];
        chunks.push(hasDataLength(dataLength) ? "".concat(dataType, "(").concat(dataLength, ")") : dataType);
        return chunks.join(' ');
    };
    STRING.prototype.cast = function (value) {
        if (typeof value === 'object' && value != null) {
            return global.JSON.stringify(value);
        }
        if (value == null) {
            return value;
        }
        return '' + value;
    };
    STRING.prototype.uncast = function (value) {
        if (value == null || value instanceof Raw)
            return value;
        return '' + value;
    };
    return STRING;
}(DataType));
var CHAR = /** @class */ (function (_super) {
    __extends(CHAR, _super);
    function CHAR(dataLength) {
        if (dataLength === void 0) { dataLength = 255; }
        var _this = _super.call(this, dataLength) || this;
        _this.dataType = 'char';
        return _this;
    }
    return CHAR;
}(STRING));
var BINARY = /** @class */ (function (_super) {
    __extends(BINARY, _super);
    function BINARY(dataLength) {
        if (dataLength === void 0) { dataLength = 255; }
        var _this = _super.call(this) || this;
        _this.dataLength = dataLength;
        _this.dataType = 'binary';
        return _this;
    }
    BINARY.prototype.toSqlString = function () {
        var dataLength = this.dataLength;
        var dataType = this.dataType.toUpperCase();
        var chunks = [];
        chunks.push(hasDataLength(dataLength) ? "".concat(dataType, "(").concat(dataLength, ")") : dataType);
        return chunks.join(' ');
    };
    BINARY.prototype.cast = function (value) {
        if (value == null)
            return value;
        if (Buffer.isBuffer(value))
            return value;
        return Buffer.from(value);
    };
    return BINARY;
}(DataType));
var VARBINARY = /** @class */ (function (_super) {
    __extends(VARBINARY, _super);
    function VARBINARY(dataLength) {
        var _this = _super.call(this, dataLength) || this;
        _this.dataType = 'varbinary';
        return _this;
    }
    return VARBINARY;
}(BINARY));
/**
 * ZEROFILL is deprecated
 * - https://dev.mysql.com/doc/refman/8.0/en/integer-types.html
 * @example
 * INTEGER
 * INTEGER.UNSIGNED
 * INTEGER.UNSIGNED.ZEROFILL
 * INTEGER(10)
 * @param {number} dataLength
 */
var INTEGER = /** @class */ (function (_super) {
    __extends(INTEGER, _super);
    function INTEGER(dataLength, unsigned, zerofill) {
        var _this = _super.call(this) || this;
        _this.dataLength = dataLength;
        _this.dataType = 'integer';
        _this.unsigned = unsigned;
        _this.zerofill = zerofill;
        return _this;
    }
    Object.defineProperty(INTEGER.prototype, "UNSIGNED", {
        get: function () {
            this.unsigned = true;
            return this;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(INTEGER.prototype, "ZEROFILL", {
        get: function () {
            this.zerofill = true;
            return this;
        },
        enumerable: false,
        configurable: true
    });
    INTEGER.prototype.toSqlString = function () {
        var _a = this, dataLength = _a.dataLength, unsigned = _a.unsigned, zerofill = _a.zerofill;
        var dataType = this.dataType.toUpperCase();
        var chunks = [];
        chunks.push(hasDataLength(dataLength) ? "".concat(dataType, "(").concat(dataLength, ")") : dataType);
        if (unsigned)
            chunks.push('UNSIGNED');
        if (zerofill)
            chunks.push('ZEROFILL');
        return chunks.join(' ');
    };
    INTEGER.prototype.cast = function (value) {
        var result = Number(value);
        if (value == null || Number.isNaN(result))
            return value;
        return result;
    };
    INTEGER.prototype.uncast = function (value, strict) {
        if (strict === void 0) { strict = true; }
        var originValue = value;
        if (value == null || value instanceof Raw)
            return value;
        if (typeof value === 'string')
            value = parseInt(value, 10);
        if (Number.isNaN(value)) {
            if (strict)
                throw new Error(util.format('invalid integer: %s', originValue));
            return originValue;
        }
        return value;
    };
    return INTEGER;
}(DataType));
/**
 * 8 bit integer
 * @example
 * TINYINT
 * TINYINT.UNSIGNED
 * TINYINT(1)
 * @param {number} dataLength
 */
var TINYINT = /** @class */ (function (_super) {
    __extends(TINYINT, _super);
    function TINYINT(dataLength, unsigned, zerofill) {
        var _this = _super.call(this, dataLength, unsigned, zerofill) || this;
        _this.dataType = 'tinyint';
        return _this;
    }
    return TINYINT;
}(INTEGER));
/**
 * 16 bit integer
 * @example
 * SMALLINT
 * SMALLINT.UNSIGNED
 * SMALLINT(2)
 * @param {number} dataLength
 */
var SMALLINT = /** @class */ (function (_super) {
    __extends(SMALLINT, _super);
    function SMALLINT(dataLength, unsigned, zerofill) {
        var _this = _super.call(this, dataLength, unsigned, zerofill) || this;
        _this.dataType = 'smallint';
        return _this;
    }
    return SMALLINT;
}(INTEGER));
/**
 * 24 bit integer
 * @example
 * MEDIUMINT
 * MEDIUMINT.UNSIGNED
 * MEDIUMINT(3)
 * @param {number} dataLength
 */
var MEDIUMINT = /** @class */ (function (_super) {
    __extends(MEDIUMINT, _super);
    function MEDIUMINT(dataLength, unsigned, zerofill) {
        var _this = _super.call(this, dataLength, unsigned, zerofill) || this;
        _this.dataType = 'mediumint';
        return _this;
    }
    return MEDIUMINT;
}(INTEGER));
/**
 * 64 bit integer
 * @example
 * BIGINT
 * BIGINT.UNSIGNED
 * BIGINT(8)
 * @param {number} dataLength
 */
var BIGINT = /** @class */ (function (_super) {
    __extends(BIGINT, _super);
    function BIGINT(dataLength, unsigned, zerofill) {
        var _this = _super.call(this, dataLength, unsigned, zerofill) || this;
        _this.dataType = 'bigint';
        return _this;
    }
    return BIGINT;
}(INTEGER));
/**
 * fixed-point decimal types
 * @example
 * DECIMAL
 * DECIMAL.UNSIGNED
 * DECIMAL(5, 2)
 * @param {number} precision
 * @param {number} scale
 * - https://dev.mysql.com/doc/refman/8.0/en/fixed-point-types.html
 */
var DECIMAL = /** @class */ (function (_super) {
    __extends(DECIMAL, _super);
    function DECIMAL(precision, scale) {
        var _this = _super.call(this) || this;
        _this.dataType = 'decimal';
        _this.precision = precision;
        _this.scale = scale;
        return _this;
    }
    DECIMAL.prototype.toSqlString = function () {
        var _a = this, precision = _a.precision, scale = _a.scale, unsigned = _a.unsigned, zerofill = _a.zerofill;
        var dataType = this.dataType.toUpperCase();
        var chunks = [];
        if (precision && precision > 0 && scale != null && scale >= 0) {
            chunks.push("".concat(dataType, "(").concat(precision, ",").concat(scale, ")"));
        }
        else if (precision && precision > 0) {
            chunks.push("".concat(dataType, "(").concat(precision, ")"));
        }
        else {
            chunks.push(dataType);
        }
        if (unsigned)
            chunks.push('UNSIGNED');
        if (zerofill)
            chunks.push('ZEROFILL');
        return chunks.join(' ');
    };
    return DECIMAL;
}(INTEGER));
var rDateFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:[,.]\d{3,6}){0,1}$/;
var DATE = /** @class */ (function (_super) {
    __extends(DATE, _super);
    function DATE(precision, timezone) {
        if (timezone === void 0) { timezone = true; }
        var _this = _super.call(this) || this;
        _this.timezone = true;
        _this.dataType = 'datetime';
        _this.precision = precision;
        // PostgreSQL enables timestamp with or without time zone
        // - https://www.postgresql.org/docs/9.5/datatype-datetime.html
        _this.timezone = timezone;
        return _this;
    }
    DATE.prototype.toSqlString = function () {
        var precision = this.precision;
        var dataType = this.dataType.toUpperCase();
        if (precision != null && precision >= 0)
            return "".concat(dataType, "(").concat(precision, ")");
        return dataType;
    };
    DATE.prototype._round = function (value) {
        var precision = this.precision;
        if (precision != null && precision < 3 && value instanceof Date) {
            var divider = Math.pow(10, (3 - precision));
            return new Date(Math.round(value.getTime() / divider) * divider);
        }
        return value;
    };
    DATE.prototype.cast = function (value) {
        var original = value;
        if (value == null)
            return value;
        if (!(value instanceof Date))
            value = new Date(value);
        if (isNaN(value.getTime()))
            return original;
        return this._round(value);
    };
    DATE.prototype.uncast = function (value) {
        var originValue = value;
        // type narrowing doesn't handle `return value` correctly
        if (value == null)
            return value;
        if (value instanceof Raw)
            return value;
        // Date | Moment
        if (typeof value === 'object' && 'toDate' in value)
            value = value.toDate();
        // @deprecated
        // vaguely standard date formats such as 2021-10-15 15:50:02,548
        if (typeof value === 'string' && rDateFormat.test(value)) {
            // 2021-10-15 15:50:02,548 => 2021-10-15 15:50:02.548,
            value = dayjs("".concat(value.replace(',', '.'))).toDate();
        }
        // 1634611135776
        // '2021-10-15T08:38:43.877Z'
        if (!(value instanceof Date))
            value = dayjs(value).toDate();
        if (isNaN(value))
            throw new Error(util.format('invalid date: %s', originValue));
        return this._round(value);
    };
    return DATE;
}(DataType));
var DATEONLY = /** @class */ (function (_super) {
    __extends(DATEONLY, _super);
    function DATEONLY() {
        var _this = _super.call(this) || this;
        _this.dataType = 'date';
        _this.precision = null;
        _this.timezone = false;
        return _this;
    }
    DATEONLY.prototype.toSqlString = function () {
        return this.dataType.toUpperCase();
    };
    DATEONLY.prototype._round = function (value) {
        if (value instanceof Date) {
            return new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }
        return value;
    };
    return DATEONLY;
}(DATE));
var BOOLEAN = /** @class */ (function (_super) {
    __extends(BOOLEAN, _super);
    function BOOLEAN() {
        var _this = _super.call(this) || this;
        _this.dataType = 'boolean';
        return _this;
    }
    BOOLEAN.prototype.toSqlString = function () {
        return this.dataType.toUpperCase();
    };
    BOOLEAN.prototype.cast = function (value) {
        if (value == null)
            return value;
        return Boolean(value);
    };
    return BOOLEAN;
}(DataType));
var TEXT = /** @class */ (function (_super) {
    __extends(TEXT, _super);
    function TEXT(length) {
        if (length === void 0) { length = LENGTH_VARIANTS.empty; }
        var _this = this;
        if (!Object.values(LENGTH_VARIANTS).includes(length)) {
            throw new Error("invalid text length: ".concat(length));
        }
        _this = _super.call(this) || this;
        _this.dataType = 'text';
        _this.dataLength = length;
        return _this;
    }
    TEXT.prototype.toSqlString = function () {
        return [this.dataLength, this.dataType].join('').toUpperCase();
    };
    return TEXT;
}(STRING));
var BLOB = /** @class */ (function (_super) {
    __extends(BLOB, _super);
    function BLOB(length) {
        if (length === void 0) { length = LENGTH_VARIANTS.empty; }
        var _this = this;
        if (!Object.values(LENGTH_VARIANTS).includes(length)) {
            throw new Error("invalid blob length: ".concat(length));
        }
        _this = _super.call(this) || this;
        _this.dataType = 'blob';
        _this.dataLength = length;
        return _this;
    }
    BLOB.prototype.toSqlString = function () {
        return [this.dataLength, this.dataType].join('').toUpperCase();
    };
    BLOB.prototype.cast = function (value) {
        if (value == null)
            return value;
        if (Buffer.isBuffer(value))
            return value;
        return Buffer.from(value);
    };
    return BLOB;
}(DataType));
// JSON text type
var MYJSON = /** @class */ (function (_super) {
    __extends(MYJSON, _super);
    function MYJSON() {
        var _this = _super.call(this) || this;
        _this.dataType = 'text';
        return _this;
    }
    MYJSON.prototype.toSqlString = function () {
        return 'TEXT';
    };
    MYJSON.toSqlString = function () {
        return 'TEXT';
    };
    MYJSON.prototype.cast = function (value) {
        if (!value)
            return value;
        // type === JSONB
        if (typeof value === 'object')
            return value;
        try {
            return global.JSON.parse(value);
        }
        catch (err) {
            console.error(new Error("unable to cast ".concat(value, " to JSON")));
            return value;
        }
    };
    MYJSON.prototype.uncast = function (value) {
        if (value == null || value instanceof Raw)
            return value;
        return global.JSON.stringify(value);
    };
    return MYJSON;
}(DataType));
// JSON binary type, available in postgreSQL or mySQL 5.7 +
// - https://dev.mysql.com/doc/refman/8.0/en/json.html
// - https://www.postgresql.org/docs/9.4/datatype-json.html
var JSONB = /** @class */ (function (_super) {
    __extends(JSONB, _super);
    function JSONB() {
        var _this = _super.call(this) || this;
        _this.dataType = 'json';
        return _this;
    }
    JSONB.prototype.toSqlString = function () {
        return 'JSON';
    };
    JSONB.toSqlString = function () {
        return 'JSON';
    };
    return JSONB;
}(MYJSON));
var VIRTUAL = /** @class */ (function (_super) {
    __extends(VIRTUAL, _super);
    function VIRTUAL() {
        var _this = _super.call(this) || this;
        _this.virtual = true;
        _this.dataType = 'virtual';
        _this.virtual = true;
        return _this;
    }
    VIRTUAL.prototype.toSqlString = function () {
        return 'VIRTUAL';
    };
    VIRTUAL.toSqlString = function () {
        return 'VIRTUAL';
    };
    return VIRTUAL;
}(DataType));
var AllDataTypes = {
    CHAR: CHAR,
    STRING: STRING,
    TINYINT: TINYINT,
    SMALLINT: SMALLINT,
    MEDIUMINT: MEDIUMINT,
    INTEGER: INTEGER,
    BIGINT: BIGINT,
    DECIMAL: DECIMAL,
    DATE: DATE,
    DATEONLY: DATEONLY,
    BOOLEAN: BOOLEAN,
    TEXT: TEXT,
    BLOB: BLOB,
    JSON: MYJSON,
    JSONB: JSONB,
    BINARY: BINARY,
    VARBINARY: VARBINARY,
    VIRTUAL: VIRTUAL,
};
var DataTypes = /** @class */ (function () {
    function DataTypes() {
    }
    DataTypes.findType = function (columnType) {
        var _a = this, CHAR = _a.CHAR, STRING = _a.STRING, TEXT = _a.TEXT, DATE = _a.DATE, DATEONLY = _a.DATEONLY, TINYINT = _a.TINYINT, SMALLINT = _a.SMALLINT, MEDIUMINT = _a.MEDIUMINT, INTEGER = _a.INTEGER, BIGINT = _a.BIGINT, DECIMAL = _a.DECIMAL, BOOLEAN = _a.BOOLEAN, BINARY = _a.BINARY, VARBINARY = _a.VARBINARY, BLOB = _a.BLOB;
        var res = columnType === null || columnType === void 0 ? void 0 : columnType.match(/(\w+)(?:\((\d+)(?:,(\d+))?\))?/);
        if (!res) {
            throw new Error("Unknown columnType ".concat(columnType));
        }
        var _b = __read(res), dataType = _b[1], matches = _b.slice(2);
        var params = [];
        for (var i = 0; i < matches.length; i++) {
            if (matches[i] != null)
                params[i] = parseInt(matches[i], 10);
        }
        switch (dataType) {
            case 'char':
                return new (CHAR.bind.apply(CHAR, __spreadArray([void 0], __read(params), false)))();
            case 'varchar':
                return new (STRING.bind.apply(STRING, __spreadArray([void 0], __read(params), false)))();
            case 'tinytext':
                return new TEXT(LENGTH_VARIANTS.tiny);
            // longtext is only for MySQL
            case 'longtext':
                return new TEXT(LENGTH_VARIANTS.long);
            case 'mediumtext':
                return new TEXT(LENGTH_VARIANTS.medium);
            case 'text':
                return new TEXT();
            case 'date':
                return new DATEONLY();
            case 'datetime':
            case 'timestamp':
                // new DATE(precision)
                return new (DATE.bind.apply(DATE, __spreadArray([void 0], __read(params), false)))();
            case 'decimal':
                return new (DECIMAL.bind.apply(DECIMAL, __spreadArray([void 0], __read(params), false)))();
            case 'int':
            case 'integer':
            case 'numeric':
                return new (INTEGER.bind.apply(INTEGER, __spreadArray([void 0], __read(params), false)))();
            case 'mediumint':
                return new (MEDIUMINT.bind.apply(MEDIUMINT, __spreadArray([void 0], __read(params), false)))();
            case 'smallint':
                return new (SMALLINT.bind.apply(SMALLINT, __spreadArray([void 0], __read(params), false)))();
            case 'tinyint':
                return new (TINYINT.bind.apply(TINYINT, __spreadArray([void 0], __read(params), false)))();
            case 'bigint':
                return new (BIGINT.bind.apply(BIGINT, __spreadArray([void 0], __read(params), false)))();
            case 'boolean':
                return new BOOLEAN();
            // mysql only
            case 'binary':
            // postgres only
            case 'bytea':
                return new (BINARY.bind.apply(BINARY, __spreadArray([void 0], __read(params), false)))();
            // mysql only
            case 'varbinary':
                return new (VARBINARY.bind.apply(VARBINARY, __spreadArray([void 0], __read(params), false)))();
            case 'longblob':
                return new BLOB(LENGTH_VARIANTS.long);
            case 'mediumblob':
                return new BLOB(LENGTH_VARIANTS.medium);
            case 'blob':
                return new BLOB();
            case 'tinyblob':
                return new BLOB(LENGTH_VARIANTS.tiny);
            default:
                throw new Error("Unexpected data type ".concat(dataType));
        }
    };
    Object.defineProperty(DataTypes, "invokable", {
        get: function () {
            return new Proxy(this, {
                get: function (target, p) {
                    var value = target[p];
                    if (AllDataTypes.hasOwnProperty(p))
                        return invokableFunc(value);
                    return value;
                }
            });
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Check if params is instance of DataType or not
     * @param {*} params
     * @returns {boolean}
     */
    DataTypes.is = function (params) {
        return params instanceof DataType;
    };
    DataTypes.CHAR = CHAR;
    DataTypes.STRING = STRING;
    DataTypes.TINYINT = TINYINT;
    DataTypes.SMALLINT = SMALLINT;
    DataTypes.MEDIUMINT = MEDIUMINT;
    DataTypes.INTEGER = INTEGER;
    DataTypes.BIGINT = BIGINT;
    DataTypes.DECIMAL = DECIMAL;
    DataTypes.DATE = DATE;
    DataTypes.TEXT = TEXT;
    DataTypes.BLOB = BLOB;
    DataTypes.JSON = MYJSON;
    DataTypes.JSONB = JSONB;
    DataTypes.BINARY = BINARY;
    DataTypes.VARBINARY = VARBINARY;
    DataTypes.VIRTUAL = VIRTUAL;
    DataTypes.DATEONLY = DATEONLY;
    DataTypes.BOOLEAN = BOOLEAN;
    return DataTypes;
}());
export var invokable = DataTypes.invokable;
export default DataTypes;
//# sourceMappingURL=data_types.js.map